'use client';

import { useEffect, useRef, useState } from 'react';
import { useQueryState } from 'nuqs';
import { usePrivy } from '@privy-io/react-auth';
import { WalletReadyState } from '@solana/wallet-adapter-base';
import { Wallet } from '@solana/wallet-adapter-react';
import useCurrentWalletAdapter from 'src/hooks/useCurrentWalletAdapter';
import useDriftClient from 'src/hooks/useDriftClient';
import useHasConnectedWalletBefore from 'src/hooks/useHasConnectedWalletBefore';
import useWalletContext from 'src/hooks/useWalletContext';
import { WALLET_NAME_LOCALSTORAGE_KEY } from 'src/hooks/useHandleSelectWalletProvider';
import { INITIAL_AUTOCONNECT_TIMEOUT_MS } from 'src/providers/app/AppInitialLoadProvider/consts';
import { usePrivyWalletContext } from 'src/providers/wallets/PrivyWalletProvider';
import useFeatureFlagStore from 'src/stores/featureFlag/useFeatureFlagStore';
import { SetTimeoutResult } from 'src/utils/UtilityTypes';

export interface UseWalletAutoconnectPlan {
	matchingWallet: Wallet | undefined;
	shouldAttemptWalletAdapterAutoconnect: boolean;
	shouldAttemptPrivyNativeAutoconnect: boolean;
	shouldAwaitWalletAdapterAutoconnect: boolean;
	shouldAwaitPrivyNativeAutoconnect: boolean;
}

/**
 * Determines whether we should attempt to autoconnect the user's wallet.
 */
export default function useWalletAutoconnectPlan(): UseWalletAutoconnectPlan {
	const driftClient = useDriftClient();
	const currentWalletAdapter = useCurrentWalletAdapter();
	const [walletHasConnectedBefore] = useHasConnectedWalletBefore();
	const walletContext = useWalletContext();

	const isPrivyNativeAccountsEnabled = useFeatureFlagStore((s) =>
		s.flagEnabled('ENABLE_PRIVY_NATIVE_ACCOUNTS')
	);
	const { ready: privyReady, authenticated } = usePrivy();
	const { embeddedWallet, sessionMode } = usePrivyWalletContext();

	const [hasPrivyHydratedOnce, setHasPrivyHydratedOnce] = useState(false);
	const [hasPrivyOAuthResolutionTimedOut, setHasPrivyOAuthResolutionTimedOut] =
		useState(false);

	const privyAuthResolutionTimerRef = useRef<SetTimeoutResult | null>(null);

	// Need to clean the wallet name because it gets saved with weird characters sometimes.
	const previouslyConnectedWalletName = getPreviouslyConnectedWalletName();
	const cleanedPreviouslyConnectedWalletName = cleanWalletName(
		previouslyConnectedWalletName
	);

	const matchingWallet = walletContext.wallets.find(
		(wallet) =>
			cleanWalletName(wallet.adapter.name) ===
			cleanedPreviouslyConnectedWalletName
	);

	const shouldAttemptWalletAdapterAutoconnect =
		!!matchingWallet &&
		walletHasConnectedBefore &&
		matchingWallet.readyState === WalletReadyState.Installed;

	// In the case of wallet adapter, this is just an alias.
	const shouldAwaitWalletAdapterAutoconnect =
		shouldAttemptWalletAdapterAutoconnect;

	// ---
	// Below is all Privy logic, which is a bit more complex because we have to
	// handle Privy's SDK loading and Google OAuth redirect / callback resolution.
	// ---

	const isEoaLinkedSession = sessionMode === 'eoa-linked';

	const isWalletAdapterConnected =
		currentWalletAdapter.connected && !!currentWalletAdapter.publicKey;

	const shouldAttemptPrivyNativeAutoconnect =
		isPrivyNativeAccountsEnabled &&
		privyReady &&
		authenticated &&
		!isEoaLinkedSession &&
		!!driftClient &&
		!!embeddedWallet &&
		!isWalletAdapterConnected;

	const shouldAwaitPrivyReadyBootstrap =
		!privyReady && walletHasConnectedBefore && !hasPrivyHydratedOnce;

	// This should be present when the user is redirected back to Drift from OAuth
	// login. We check this so we don't await OAuth resolution if the user cancels
	// the OAuth flow (e.g. clicks back button from Google auth page).
	const [privyOAuthCode] = useQueryState('privy_oauth_code');

	const shouldAwaitPrivyOAuthResolution =
		!authenticated && !!privyOAuthCode && !hasPrivyOAuthResolutionTimedOut;

	const shouldAwaitPrivyNativeAutoconnect =
		isPrivyNativeAccountsEnabled &&
		!isEoaLinkedSession &&
		!isWalletAdapterConnected &&
		(shouldAwaitPrivyReadyBootstrap ||
			shouldAwaitPrivyOAuthResolution ||
			(privyReady && authenticated));

	// Configure a timeout for Privy OAuth resolution. Simply a defensive measure
	// to prevent an infinite loading state if something goes wrong.
	useEffect(() => {
		// Clean up if we don't need to await Privy OAuth resolution.
		if (!shouldAwaitPrivyOAuthResolution) {
			if (privyAuthResolutionTimerRef.current) {
				clearTimeout(privyAuthResolutionTimerRef.current);
				privyAuthResolutionTimerRef.current = null;
			}

			if (hasPrivyOAuthResolutionTimedOut) {
				setHasPrivyOAuthResolutionTimedOut(false);
			}

			return;
		}

		if (
			!privyReady ||
			hasPrivyOAuthResolutionTimedOut ||
			privyAuthResolutionTimerRef.current
		) {
			return;
		}

		privyAuthResolutionTimerRef.current = setTimeout(() => {
			privyAuthResolutionTimerRef.current = null;
			setHasPrivyOAuthResolutionTimedOut(true);
		}, INITIAL_AUTOCONNECT_TIMEOUT_MS);
	}, [
		privyReady,
		hasPrivyOAuthResolutionTimedOut,
		shouldAwaitPrivyOAuthResolution,
	]);

	// We only want to run Privy logic on initial load.
	useEffect(() => {
		if (privyReady && !hasPrivyHydratedOnce) {
			setHasPrivyHydratedOnce(true);
		}
	}, [privyReady, hasPrivyHydratedOnce]);

	// Clean up on unmount.
	useEffect(() => {
		return () => {
			if (privyAuthResolutionTimerRef.current) {
				clearTimeout(privyAuthResolutionTimerRef.current);

				privyAuthResolutionTimerRef.current = null;
			}
		};
	}, []);

	return {
		matchingWallet,
		shouldAttemptWalletAdapterAutoconnect,
		shouldAttemptPrivyNativeAutoconnect,
		shouldAwaitWalletAdapterAutoconnect,
		shouldAwaitPrivyNativeAutoconnect,
	};
}

function getPreviouslyConnectedWalletName() {
	if (typeof window === 'undefined') {
		return undefined;
	}

	return localStorage.getItem(WALLET_NAME_LOCALSTORAGE_KEY);
}

function cleanWalletName(walletName: string | null | undefined) {
	return walletName?.replace(/[^a-zA-Z0-9]/g, '') ?? '';
}
